/**
 * serviceBookingService — bookings (guest on a service) + guest schedule.
 * apiClient auto-transforms camelCase↔snake_case; DRF Decimals arrive as strings.
 */

import { apiClient } from '@/shared/services/api';
import type { ServiceBooking, ServiceBookingInput, GuestScheduleItem } from '../types';

function num(v: unknown): number {
  return v == null || v === '' ? 0 : Number(v);
}
function normalizeBooking(b: ServiceBooking): ServiceBooking {
  return { ...b, priceCharged: num(b.priceCharged) };
}
function normalizeItem(i: GuestScheduleItem): GuestScheduleItem {
  return { ...i, priceCharged: num(i.priceCharged) };
}

export async function getServiceBookings(filters?: { guest?: string; run?: string; booking?: string; status?: string; date?: string }): Promise<ServiceBooking[]> {
  const params: Record<string, string> = { page_size: '200' };
  if (filters?.guest) params.guest = filters.guest;
  if (filters?.run) params.run = filters.run;
  if (filters?.booking) params.booking = filters.booking;
  if (filters?.status) params.status = filters.status;
  if (filters?.date) params.date = filters.date;
  const res = await apiClient.get<{ results: ServiceBooking[] }>('/api/v1/services/bookings/', { params });
  return res.data.results.map(normalizeBooking);
}

export async function getServiceBooking(id: string): Promise<ServiceBooking> {
  const res = await apiClient.get<ServiceBooking>(`/api/v1/services/bookings/${id}/`);
  return normalizeBooking(res.data);
}

export async function createServiceBooking(data: ServiceBookingInput): Promise<ServiceBooking> {
  const res = await apiClient.post<ServiceBooking>('/api/v1/services/bookings/', data);
  return normalizeBooking(res.data);
}

export async function cancelServiceBooking(id: string): Promise<ServiceBooking> {
  const res = await apiClient.post<ServiceBooking>(`/api/v1/services/bookings/${id}/cancel/`, {});
  return normalizeBooking(res.data);
}

export async function moveServiceBooking(id: string, newRunId: string): Promise<ServiceBooking> {
  const res = await apiClient.post<ServiceBooking>(`/api/v1/services/bookings/${id}/move/`, { run: newRunId });
  return normalizeBooking(res.data);
}

export async function checkInServiceBooking(id: string): Promise<ServiceBooking> {
  const res = await apiClient.post<ServiceBooking>(`/api/v1/services/bookings/${id}/check-in/`, {});
  return normalizeBooking(res.data);
}

/** Unified schedule (activities + services) for a guest — the conflict/timeline source. */
export async function getGuestSchedule(guestId: string, dateFrom?: string, dateTo?: string): Promise<GuestScheduleItem[]> {
  const params: Record<string, string> = { guest: guestId };
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await apiClient.get<GuestScheduleItem[]>('/api/v1/services/bookings/guest-schedule/', { params });
  return res.data.map(normalizeItem);
}
